import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import RefreshIcon from '@mui/icons-material/Refresh';
import { api } from '../api';

const EmailVerificationRequired = () => {
  const history = useHistory();
  const { currentUser, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleResendVerification = async () => {
    setLoading(true);
    setError('');
    setMessage('');
    
    try {
      // Use the unauthenticated endpoint that only requires email
      // This works even if the user doesn't have a valid token
      const userEmail = currentUser?.email;
      if (!userEmail) {
        setError('Unable to determine your email address. Please try logging in again.');
        setLoading(false);
        return;
      }
      
      const response = await api.post('/auth/resend-verification-by-email', { 
        email: userEmail 
      });
      setMessage('Verification email sent! Please check your inbox.');
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to send verification email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    history.push('/login');
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <EmailIcon sx={{ fontSize: 60, color: 'warning.main', mb: 2 }} />
          <Typography variant="h5" component="h1" gutterBottom align="center">
            Email Verification Required
          </Typography>
          <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 3 }}>
            Please verify your email address to continue using EventSpot.
          </Typography>
          
          {currentUser && (
            <Alert severity="info" sx={{ mb: 3, width: '100%' }}>
              <Typography variant="body2">
                A verification email was sent to <strong>{currentUser.email}</strong>
              </Typography>
            </Alert>
          )}

          {message && (
            <Alert severity="success" sx={{ mb: 2, width: '100%' }}>
              {message}
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2, width: '100%' }}>
              {error}
            </Alert>
          )}

          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
            <Button
              variant="contained"
              fullWidth
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
              onClick={handleResendVerification}
              disabled={loading}
            >
              {loading ? 'Sending...' : 'Resend Verification Email'}
            </Button>
            
            <Button
              variant="outlined"
              fullWidth
              onClick={handleLogout}
            >
              Logout
            </Button>
          </Box>

          <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 3 }}>
            Didn't receive the email? Check your spam folder or click "Resend Verification Email" above.
          </Typography>
        </Paper>
      </Box>
    </Container>
  );
};

export default EmailVerificationRequired;

